import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";

import { Observable, Subject, combineLatest, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { Events, EventBusService } from "../../../../technical-modules/events/services/event-bus.service";

import { PlanPreventionService } from '../../services/plan-prevention.service';
import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';


import { applicationConstants } from '../../../../app.constants';
import { RepresentantEntity } from '../../entities/representant.entity';
import { SignatairePdpEntity } from '../../entities/signataire-pdp.entity';
import { PlanPreventionEntity } from '../../entities/plan-prevention.entity';
import { Destinataire, TypeActeur } from '../../entities/destinataire.entity';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { ValideurSignataireEntity, TypeValideur } from '../../entities/valideur-signataire.entity';
import { IntervenantEntity } from '../../../humain/intervenant/entities/intervenant.entity';

type ReferenceEntrepriseChoix = {
	[id: number]: boolean;
}
@Component({
	selector: 'gestion-signature-pdp-general',
	templateUrl: 'gestion-signature-pdp-general.html',
	styleUrls: ['gestion-signature-pdp-general.css'],
})
export class GestionSignaturePdpGeneralComponent {
	
	// gestion affichage de la fenetre popup
	displaySubject = new Subject<boolean>();
	
	pdp: PlanPreventionEntity = new PlanPreventionEntity();
	
	waitingForServer = false;

	valideursDuPdp: ValideurSignataireEntity[] = [];
	representantsDuPdp: RepresentantEntity[] = [];
	intervenantsDuPdp: SignatairePdpEntity[] = [];
	valideursPouvantSignerCePdp: SignatairePdpEntity[] = [];
	representantsPouvantSignerCePdp: SignatairePdpEntity[] = [];
	intervenantsPouvantSignerCePdp: SignatairePdpEntity[] = [];
	
	gererValideursCommands = new Subject<[SignatairePdpEntity[], SignatairePdpEntity[]]>();
	gererRepresentantsCommands = new Subject<[SignatairePdpEntity[], SignatairePdpEntity[]]>();
	gererIntervenantsCommands = new Subject<[SignatairePdpEntity[], SignatairePdpEntity[]]>();

	selectedEntrepriseForValideurDialog!: EntrepriseTLR | null;
	selectedEntrepriseForRepresentantDialog!: EntrepriseTLR | null;
	selectedEntrepriseForIntervenantDialog!: EntrepriseTLR | null;
	
	entreprises: EntrepriseTLR[] = [];
	entrepriseUtilisatrice!: EntrepriseTLR | null;
	entrepriseExterneIds: number[] = [];
	entrepriseValideurIds: number[] = [];
	
	sendAllValideursCb: boolean = false;
	signAllValideursCb: boolean = false;
	sendAllRepresentantsCb: boolean = false;
	signAllRepresentantsCb: boolean = false;
	sendAllIntervenantsCb: boolean = false;
	signAllIntervenantsCb: boolean = false;
	sendAllValideursInEntrepriseCb: ReferenceEntrepriseChoix = {};
	sendAllRepresentantsInEntrepriseCb: ReferenceEntrepriseChoix = {};
	sendAllIntervenantsInEntrepriseCb: ReferenceEntrepriseChoix = {};
	
	viewEnveloppeCommand = new Subject<string>();

	envelopeIds: string[] = [];
	hasAtLeastOneSigner: boolean = false;
	
	constructor( //
		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(IntervenantService) intervenantService: IntervenantService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(PlanPreventionService) private planPreventionService: PlanPreventionService,
	) {
		// Lors de la reception d'une demande de signature de pdp
		this.signCommandsSubject.pipe(
			mergeMap(
				(pdp) => {
					console.log('gestion des signature pour un pdp general');
					
					// reinit des valeurs pour un affichage propre
					this.reinitGlobals();
					
					// charger les intervenants, représentants, et valideurs du PDP
					let intervenants$ = planPreventionService.getIntervenants(pdp.id);
					let valideursEtRepresentant$ = planPreventionService.getValideursEtRepresentants(pdp.id);
					let entreprises$ = planPreventionService.getTravaux(pdp.id).pipe(
						map(
							(pdpTravaux) => pdpTravaux.entreprisesExterieures.map(
								(ee) => {
									let entTlr = new EntrepriseTLR();
									entTlr.id = ee.idEntrepriseExt;
									entTlr.raisonSociale = ee.raisonSociale;
									entTlr.nom = ee.nomEntreprise;
									return entTlr;
								}
							)
						)
					);
					let referentParc$ = intervenantService.getIntervenantById(pdp.referentParcId);
					return combineLatest([of(pdp), valideursEtRepresentant$, intervenants$, entreprises$, referentParc$]);
				}
			),
			mergeMap(
				(data) => {
					this.entreprises = data[3];

					if (data[4]) {
						// charger les intervenants de l'entreprise utilisatrice
						intervenantService.getIntervenantsForEntreprise([data[4].idEntreprise]).subscribe(
							(intervenants) => {
								intervenants.forEach( (intervenant) => {
										let signataire: SignatairePdpEntity = this.intervenantToSignataire(intervenant);
										this.valideursPouvantSignerCePdp.push(signataire);
									}
								);
								this.valideursPouvantSignerCePdp.sort(
									(s1, s2) => s1.nomEntreprise.localeCompare(s2.nomEntreprise) || s1.nom.localeCompare(s2.nom)
								);
							}
						);
						this.entrepriseUtilisatrice = new EntrepriseTLR();
						this.entrepriseUtilisatrice.id = data[4].idEntreprise;
						this.entrepriseUtilisatrice.nom = data[4].nomEntreprise;
						this.entreprises.push(this.entrepriseUtilisatrice);
						this.entrepriseValideurIds.push(data[4].idEntreprise);
					}
					
					this.entrepriseExterneIds = this.entreprises.map( e => e.id );
					this.entrepriseValideurIds = this.entreprises.map( e => e.id );
					if (this.entreprises && this.entreprises.length) {
						// charger les intervenants lies aux entreprises participant à ce PDP
						intervenantService.getIntervenantsForEntreprise(this.entreprises.map($ => $.id)).subscribe(
							(intervenants) => intervenants.forEach(
								(intervenant) => {
									let signataire: SignatairePdpEntity = this.intervenantToSignataire(intervenant);
									this.intervenantsPouvantSignerCePdp.push(signataire);
									this.representantsPouvantSignerCePdp.push(signataire);
									this.valideursPouvantSignerCePdp.push(signataire);
								}
							)
						);
					}
					
					if (data[1] && data[1].valideurSignataires) {
						data[1].valideurSignataires.forEach(v => {
							v.emailStyle = v.email && v.email.trim() && applicationConstants.emailRegexp.test(v.email) ? '' : 'error';
							v.updatable = v.docusignEnvelopeId || data[0].obsolete || data[0].expired ? false : true;
							this.valideursDuPdp.push(v);
						});
					}
					if (data[2] && data[2].signataires) {
						data[2].signataires.forEach(s => {
							s.emailStyle = s.email && s.email.trim() && applicationConstants.emailRegexp.test(s.email) ? '' : 'error';
							s.updatable = s.envelopeId || data[0].obsolete || data[0].expired ? false : true;
							this.intervenantsDuPdp.push(s);
						});
					}
					if (data[1] && data[1].representants) {
						data[1].representants.forEach(r => {
							r.isIntervenant = this.intervenantsDuPdp.some( $ => $.id === r.intervenantId );
							r.emailStyle = r.email && r.email.trim() && applicationConstants.emailRegexp.test(r.email) ? '' : 'error';
							r.updatable = r.docusignEnvelopeId || data[0].obsolete || data[0].expired ? false : true;
							this.checkIntervenantsSubalterneWhenRepresentantDocusigned(r);
							this.representantsDuPdp.push(r);
						});
					}
					
					// cocher les cases entreprises dans la liste des valideurs
					this.refreshValideursSendCbs();
					this.refreshRepresentantsSendCbs();
					this.refreshIntervenantsSendCbs();
					
					// constituer le referentiel des envelopes
					this.assignEnvelopesIndex();

					// pour ajouter un peu plus de confort aux utilisateurs :-) on tri les tableaux avant restitution
					this.valideursDuPdp.sort(
						(s1, s2) => s1.intervenantEntrepriseNom.localeCompare(s2.intervenantEntrepriseNom) || s1.nom.localeCompare(s2.nom)
					);
					this.representantsDuPdp.sort(
						(s1, s2) => s1.intervenantEntrepriseNom.localeCompare(s2.intervenantEntrepriseNom) || s1.intervenantNom.localeCompare(s2.intervenantNom)
					);
					[ this.intervenantsPouvantSignerCePdp, this.representantsPouvantSignerCePdp, 
					  this.valideursPouvantSignerCePdp, this.intervenantsDuPdp ].forEach( tableau => {
						tableau.sort(
							(s1, s2) => s1.nomEntreprise.localeCompare(s2.nomEntreprise) || s1.nom.localeCompare(s2.nom)
						);
					});
					return combineLatest([of(data[0])]);
				}
 			)
		).subscribe(
			// on peut maintenant afficher la fenetre
			(data) => {
				this.pdp = data[0];
				console.log('valideursDuPdp: ', this.valideursDuPdp);
				console.log('representantsDuPdp: ', this.representantsDuPdp);
				console.log('intervenantsDuPdp: ', this.intervenantsDuPdp);
				console.log('valideursPouvantSignerCePdp: ', this.valideursPouvantSignerCePdp);
				console.log('representantsPouvantSignerCePdp: ', this.representantsPouvantSignerCePdp);
				console.log('intervenantsPouvantSignerCePdp: ', this.intervenantsPouvantSignerCePdp);
				this.displaySubject.next(true);
				// enlever les entreprises en doublon éventuellement
				this.entreprises = this.entreprises.filter((item, index, self) => self.findIndex((item2) => item.id === item2.id) === index);
				// trier la liste des entreprises par nom
				this.entreprises.sort(
					(e1, e2) => e1.nom.localeCompare(e2.nom)
				);
			}
 		);
	}

	@Input() id = 'pdpSignerDialog';
	
	// Liaison de l'observable servant a commander l'affichage du composant
	private signCommandsSubject = new SubjectRelay<PlanPreventionEntity>();
	@Input() set signCommands(obs: Observable<PlanPreventionEntity> | undefined) {
		this.signCommandsSubject.registerSource(obs);
	}

	// Evenements de changement sur le pdp
	@Output() pdpChanged = new EventEmitter<number>();
	
	private reinitGlobals() {
		this.waitingForServer = false;
		this.valideursDuPdp = [];
		this.representantsDuPdp = [];
		this.intervenantsDuPdp = [];
		this.valideursPouvantSignerCePdp = [];
		this.representantsPouvantSignerCePdp = [];
		this.intervenantsPouvantSignerCePdp = [];
		this.entreprises = [];
		this.entrepriseUtilisatrice = null;
		this.entrepriseExterneIds = [];
		this.entrepriseValideurIds = [];
		this.sendAllValideursCb = false;
		this.signAllValideursCb = false;
		this.sendAllValideursInEntrepriseCb = {};
		this.sendAllRepresentantsCb = false;
		this.signAllRepresentantsCb = false;
		this.sendAllRepresentantsInEntrepriseCb = {};
		this.sendAllIntervenantsCb = false;
		this.signAllIntervenantsCb = false;
		this.sendAllIntervenantsInEntrepriseCb = {};
		this.refreshValideursSendCbs();
		this.refreshRepresentantsSendCbs();
		this.refreshIntervenantsSendCbs();
		this.envelopeIds = [];
		this.hasAtLeastOneSigner = false;
	}
	
	private assignEnvelopesIndex() {

		this.envelopeIds = [];
		this.representantsDuPdp.forEach($ => {
			if ($.docusignEnvelopeId) {
				this.envelopeIds.push($.docusignEnvelopeId.toString());
			}
		});
		this.intervenantsDuPdp.forEach($ => {
			if ($.envelopeId) {
				this.envelopeIds.push($.envelopeId);
			}
		});
		this.valideursDuPdp.forEach($ => {
			if ($.docusignEnvelopeId) {
				this.envelopeIds.push($.docusignEnvelopeId.toString());
			}
		});

		this.envelopeIds = this.envelopeIds
			.filter( (value, index, array) => array.indexOf(value) === index ) // garder les valeur uniques
			.sort( ($1, $2) => $1.localeCompare($2) ); // trier du plus petit au plus grand

		this.representantsDuPdp.forEach($ => {
			if ($.docusignEnvelopeId) {
				$.envelopeIndex = this.envelopeIds.indexOf($.docusignEnvelopeId.toString()) + 1;
			}
		});
		this.intervenantsDuPdp.forEach($ => {
			if ($.envelopeId) {
				$.envelopeIndex = this.envelopeIds.indexOf($.envelopeId) + 1;
			}
		});
		this.valideursDuPdp.forEach($ => {
			if ($.docusignEnvelopeId) {
				$.envelopeIndex = this.envelopeIds.indexOf($.docusignEnvelopeId.toString()) + 1;
			}
		});

	}
	
	private intervenantToSignataire(intervenant: IntervenantEntity): SignatairePdpEntity {
		return {
			id: intervenant.id,
			nom: intervenant.nom,
			prenom: intervenant.prenom,
			nomEntreprise: intervenant.nomEntreprise,
			raisonSociale: '',
			idEntreprise: intervenant.idEntreprise,
			fonction: intervenant.cadreEmploi,
			hasSigned: false,
			hasSignedEnvelope: false,
			email: intervenant.email,
			sentToDocusign: false,
			emailStyle: intervenant.email && intervenant.email.trim() && applicationConstants.emailRegexp.test(intervenant.email) ? '' : 'error',
			envelopeId: '',
			envelopeIndex: -1,
			nature: 'intervenant',
			isIntervenant: true,
			updatable: true,
			docusignRecipientId: ''
		}
	}

	private valideurToSignataire(valideur: ValideurSignataireEntity): SignatairePdpEntity {
		return {
			id: valideur.intervenantId,
			nom: valideur.nom,
			prenom: valideur.prenom,
			nomEntreprise: valideur.intervenantEntrepriseNom,
			raisonSociale: '',
			idEntreprise: valideur.intervenantEntrepriseId,
			fonction: valideur.fonction,
			hasSigned: valideur.hasSigned,
			hasSignedEnvelope: valideur.hasSignedEnvelope,
			email: valideur.email,
			sentToDocusign: valideur.sentToDocusign,
			emailStyle: valideur.emailStyle,
			envelopeId: valideur.docusignEnvelopeId ? valideur.docusignEnvelopeId.toString() : '',
			envelopeIndex: valideur.envelopeIndex,
			nature: 'intervenant',
			isIntervenant: true,
			updatable: valideur.updatable,
			docusignRecipientId: ''
		}
	}

	private valideurToRepresentant(valideur: ValideurSignataireEntity): RepresentantEntity {
		return {
			id: valideur.id,
			intervenantId: valideur.intervenantId,
			intervenantNom: valideur.nom,
			intervenantPrenom: valideur.prenom,
			intervenantEntrepriseNom: valideur.intervenantEntrepriseNom,
			raisonSociale: '',
			intervenantEntrepriseId: valideur.intervenantEntrepriseId,
			fonction: valideur.fonction,
			hasSigned: valideur.hasSigned,
			hasSignedEnvelope: valideur.hasSignedEnvelope,
			email: valideur.email,
			sentToDocusign: valideur.sentToDocusign,
			docusignEnvelopeId: valideur.docusignEnvelopeId ? Number(valideur.docusignEnvelopeId) : -1,
			envelopeIndex: valideur.envelopeIndex,
			isIntervenant: valideur.typeValideur === TypeValideur.VALIDEUR_INTERVENANT || valideur.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT,
			planPreventionId: valideur.planPreventionId,
			dateSignature: valideur.dateSignature,
			docusignRecipientId: valideur.docusignRecipientId,
			emailStyle: valideur.emailStyle,
			updatable: valideur.updatable
		}
	}
	
	private representantToSignataire(representant: RepresentantEntity): SignatairePdpEntity {
		return {
			id: representant.intervenantId,
			nom: representant.intervenantNom,
			prenom: representant.intervenantPrenom,
			nomEntreprise: representant.intervenantEntrepriseNom,
			raisonSociale: '',
			idEntreprise: representant.intervenantEntrepriseId,
			fonction: representant.fonction,
			hasSigned: representant.hasSigned,
			hasSignedEnvelope: representant.hasSignedEnvelope,
			email: representant.email,
			sentToDocusign: representant.sentToDocusign,
			emailStyle: representant.emailStyle,
			envelopeId: representant.docusignEnvelopeId ? representant.docusignEnvelopeId.toString() : '',
			envelopeIndex: representant.envelopeIndex,
			nature: 'intervenant',
			isIntervenant: true,
			updatable: representant.updatable,
			docusignRecipientId: ''
		}
	}
	
	private intervenantToRepresentant(intervenant: SignatairePdpEntity): RepresentantEntity{
		return {
			intervenantId: intervenant.id,
			intervenantNom: intervenant.nom,
			intervenantPrenom: intervenant.prenom,
			intervenantEntrepriseNom: intervenant.nomEntreprise,
			raisonSociale: '',
			intervenantEntrepriseId: intervenant.idEntreprise,
			fonction: intervenant.fonction,
			hasSigned: intervenant.hasSigned,
			hasSignedEnvelope: intervenant.hasSignedEnvelope,
			email: intervenant.email,
			sentToDocusign: intervenant.sentToDocusign,
			docusignEnvelopeId: intervenant.envelopeId ? Number(intervenant.envelopeId) : -1,
			envelopeIndex: intervenant.envelopeIndex,
			isIntervenant: this.intervenantsDuPdp.some( $ => $.id === intervenant.id ),
			planPreventionId: this.pdp.id,
			docusignRecipientId: '',
			id: -1,
			dateSignature: '',
			emailStyle: intervenant.emailStyle,
			updatable: intervenant.updatable
		};
	}

	private intervenantToValideur(intervenant: SignatairePdpEntity): ValideurSignataireEntity{
		return {
			intervenantId: intervenant.id,
			nom: intervenant.nom,
			prenom: intervenant.prenom,
			intervenantEntrepriseNom: intervenant.nomEntreprise,
			raisonSociale: '',
			intervenantEntrepriseId: intervenant.idEntreprise,
			fonction: intervenant.fonction,
			hasSigned: intervenant.hasSigned,
			hasSignedEnvelope: intervenant.hasSignedEnvelope,
			email: intervenant.email,
			sentToDocusign: intervenant.sentToDocusign,
			docusignEnvelopeId: intervenant.envelopeId ? Number(intervenant.envelopeId) : -1,
			envelopeIndex: intervenant.envelopeIndex,
			intervenant: this.intervenantsDuPdp.some( $ => $.id === intervenant.id ),
			planPreventionId: this.pdp.id,
			docusignRecipientId: '',
			typeValideur: TypeValideur.VALIDEUR,
			id: -1,
			dateSignature: '',
			cadreEmploi: '',
			updatable: true,
			dateValidation: null,
			emailStyle: intervenant.emailStyle
		};
	}
	
	onCloseRequested() {
		this.cleanAndClose();
	}
	
	cleanAndClose() {
		this.reinitGlobals();
		this.displaySubject.next(false);
		this.pdpChanged.emit(this.pdp.id);
	}

	hasInvalidEmails() {
		// on tolere les emails vides mais pas invalides
		return (this.valideursDuPdp.some( $ => $.email !== null && $.email.trim().length != 0 && $.emailStyle === 'error' )
				|| this.representantsDuPdp.some( $ => $.email !== null && $.email.trim().length != 0 && $.emailStyle === 'error' )
				|| this.intervenantsDuPdp.some( $ => $.email !== null && $.email.trim().length != 0 && $.emailStyle === 'error' )
		);
	}
	
	hasBlankOrInvalidEmails() {
		// zero tolerance car pour docusign faut que l'email soit renseigné et au bon format
		return (this.valideursDuPdp.some( $ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign )
				|| this.representantsDuPdp.some( $ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign )
				|| this.intervenantsDuPdp.some( $ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign )
		);
	}
	
	buttonInfo(): string {
		let info = 'Envoyer l\'enveloppe vers DocuSign et sauvegarder';
		if (this.valideursDuPdp.some( $ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign )
			|| this.representantsDuPdp.some( $ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign )
			|| this.intervenantsDuPdp.some( $ => ($.emailStyle === 'error' || !$.email || $.email.trim().length === 0) && $.sentToDocusign )) {
			info = 'Vérifier l\'email des destinataires';
		}
		return info;
	}
	
	suggestableTypeValideurs(valideur: ValideurSignataireEntity) {
		if (this.entrepriseUtilisatrice && valideur.intervenantEntrepriseId === this.entrepriseUtilisatrice.id) {
			return applicationConstants.typeValideurs.filter( t => t.code === 'VALIDEUR' );
		}
		return applicationConstants.typeValideurs;
	}

	updateListeRepresentantsOnValideursChange() {
		// parcourir la liste des valideurs et  mettre à jour les representants si necessaire
		const lesValideursRepresentants = this.valideursDuPdp
			.filter($ => $.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT || $.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT);
		const representantIds = this.representantsDuPdp.map( $ => $.intervenantId );
		
		lesValideursRepresentants.filter( $ => !representantIds.includes($.intervenantId) ).forEach( missingRepresentant => {
			// pour chaque representant manquant, l'ajouter à la liste
			this.representantsDuPdp.push(this.valideurToRepresentant(missingRepresentant));
		} );
		
		// les representants qui ne sont plus intervenants doivent être decochés
		this.representantsDuPdp.forEach( representant => {
			representant.isIntervenant = this.intervenantsDuPdp.some( $=> $.id === representant.intervenantId );
		} );
		this.representantsDuPdp.sort(
			(s1, s2) => s1.intervenantEntrepriseNom.localeCompare(s2.intervenantEntrepriseNom) || s1.intervenantNom.localeCompare(s2.intervenantNom)
		);
	}

	updateListeRepresentantsOnIntervenantsChange() {
		// parcourir la liste des representants et cocher / decocher la case si necessaire
		this.representantsDuPdp.forEach( r => r.isIntervenant = this.intervenantsDuPdp.some( $ => $.id === r.intervenantId ) );
	}
	
	updateListeIntervenantsOnValideursChange() {
		// parcourir la liste des valideurs et mettre à jour les intervenants si necessaire
		const lesValideursIntervenants = this.valideursDuPdp
			.filter($ => $.typeValideur === TypeValideur.VALIDEUR_INTERVENANT || $.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT);
		const intervenantIds = this.intervenantsDuPdp.map( $ => $.id );
		
		lesValideursIntervenants.filter( $ => !intervenantIds.includes($.intervenantId) ).forEach( missingIntervenant => {
			// pour chaque intervenant manquant, l'ajouter à la liste
			this.intervenantsDuPdp.push(this.valideurToSignataire(missingIntervenant));
		} );
		this.intervenantsDuPdp.sort(
			(s1, s2) => s1.nomEntreprise.localeCompare(s2.nomEntreprise) || s1.nom.localeCompare(s2.nom)
		);
	}

	onValideursSelected	(data: [SignatairePdpEntity[], SignatairePdpEntity[]]) {
		let valideursLies = data[1];
		// retirer les valideurs qui ont été retirés
		const newlySelectedIds = valideursLies.map( $ => $.id );
		this.valideursDuPdp = this.valideursDuPdp.filter( valideur => newlySelectedIds.includes(valideur.intervenantId) );
		
		// ajouter les nouveaux valideurs
		if (valideursLies !== undefined) {
			valideursLies.forEach((value: SignatairePdpEntity) => {
				if (!this.valideursDuPdp.some( $ => $.intervenantId === value.id )) {
					this.valideursDuPdp.push(this.intervenantToValideur(value));
				}
			});
		}
		
		this.refreshValideursSendCbs();
		this.refreshValideursSignCbs();
		
		this.valideursDuPdp.sort(
			(s1, s2) => s1.intervenantEntrepriseNom.localeCompare(s2.intervenantEntrepriseNom) || s1.nom.localeCompare(s2.nom)
		);
	}

	onRepresentantsSelected	(data: [SignatairePdpEntity[], SignatairePdpEntity[]]) {
			let representantsLies = data[1];
			this.representantsDuPdp = [];
			if (representantsLies !== undefined) {
				representantsLies.forEach((value: SignatairePdpEntity) => {
					if (this.representantsDuPdp.some($ => $.intervenantEntrepriseId === value.idEntreprise)) {
						alert(value.prenom + ' '  + value.nom + ' ne sera pas marqué représentant car il y a dejà un représentant défini pour ' + value.nomEntreprise);
					} else {
						this.representantsDuPdp.push(this.intervenantToRepresentant(value));
					}
				});
			}
			
			// ceux qui sont marqués "representant" en haut et qui ne sont plus dans la liste des representants retenue en bas, doivent être décochée en haut
			this.valideursDuPdp.filter( $ => $.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT )
				.forEach( valideurSignataire => {
					if ( !this.representantsDuPdp.some( $ => $.id === valideurSignataire.intervenantId ) ) {
						// si cet intervenant n'est plus dans la liste faudra le changer en haut
						valideurSignataire.typeValideur = TypeValideur.VALIDEUR;
					}
				} );
			this.valideursDuPdp.filter( $ => $.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT )
				.forEach( valideurSignataire => {
					if ( !this.representantsDuPdp.some( $ => $.id === valideurSignataire.intervenantId ) ) {
						// si cet intervenant n'est plus dans la liste faudra le changer en haut
						valideurSignataire.typeValideur = TypeValideur.VALIDEUR_INTERVENANT;
					}
				} );
			// reciproquement : ceux qui ont été ajouté en bas et qui ne sont pas coché en haut doivent être cochés en haut
			this.representantsDuPdp.forEach( sig => {
				let valideur = this.valideursDuPdp.find(v => v.intervenantId === sig.id);
				if (valideur) {
					if (valideur.typeValideur === TypeValideur.VALIDEUR) {
						valideur.typeValideur = TypeValideur.VALIDEUR_REPRESENTANT;
					} else if (valideur.typeValideur === TypeValideur.VALIDEUR_INTERVENANT) {
						valideur.typeValideur = TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT;
					}
				}
			} );
			this.representantsDuPdp.sort(
				(s1, s2) => s1.intervenantEntrepriseNom.localeCompare(s2.intervenantEntrepriseNom) || s1.intervenantNom.localeCompare(s2.intervenantNom)
			);
	}

	onIntervenantsSelected	(data: [SignatairePdpEntity[], SignatairePdpEntity[]]) {
		this.intervenantsDuPdp = [];
		data[1].forEach( bindedIntervenant => this.intervenantsDuPdp.push(bindedIntervenant) );
		this.updateListeRepresentantsOnIntervenantsChange();
		// ceux qui sont marqués "intervenant" en haut et qui ne sont plus dans la liste des intervenants retenue en bas, doivent être décochée en haut
		this.valideursDuPdp.filter( $ => $.typeValideur === TypeValideur.VALIDEUR_INTERVENANT )
			.forEach( valideurDuPdp => {
				if ( !this.intervenantsDuPdp.some( $ => $.id === valideurDuPdp.intervenantId ) ) {
					// si cet intervenant n'est plus dans la liste faudra le changer en haut
					valideurDuPdp.typeValideur = TypeValideur.VALIDEUR;
				}
			} );
		this.valideursDuPdp.filter( $ => $.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT )
			.forEach( valideurDuPdp => {
				if ( !this.intervenantsDuPdp.some( $ => $.id === valideurDuPdp.intervenantId ) ) {
					// si cet intervenant n'est plus dans la liste faudra le changer en haut
					valideurDuPdp.typeValideur = TypeValideur.VALIDEUR_REPRESENTANT;
				}
			} );
		this.intervenantsDuPdp.forEach( sig => {
			// reciproquement : ceux qui ont été ajouté en bas et qui ne sont pas coché en haut doivent être cochés en haut
			let valideur = this.valideursDuPdp.find(v => v.intervenantId === sig.id);
			if (valideur) {
				if (valideur.typeValideur === TypeValideur.VALIDEUR) {
					valideur.typeValideur = TypeValideur.VALIDEUR_INTERVENANT;
				} else if (valideur.typeValideur === TypeValideur.VALIDEUR_REPRESENTANT) {
					valideur.typeValideur = TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT;
				}
			}
			// si cet intervenant appartient à une entreprise et que cette entreprise a un representant qui a signé, on le marque comme "a signé"
			let representantOfIntervenant = this.representantsDuPdp.filter( $ => $.intervenantEntrepriseId === sig.idEntreprise );
			if (representantOfIntervenant.length > 0 && representantOfIntervenant[0].hasSigned) {
				sig.hasSigned = true;
			}
		} );
		this.intervenantsDuPdp.sort(
			(s1, s2) => s1.nomEntreprise.localeCompare(s2.nomEntreprise) || s1.nom.localeCompare(s2.nom)
		);
		
	}
	
	addValideur() {
		let signatairesLeftColumn: SignatairePdpEntity[] = [];
		let filteredSignataire : SignatairePdpEntity[] = this.valideursPouvantSignerCePdp;
		if (this.selectedEntrepriseForValideurDialog) {
			const nomEntreprise = this.selectedEntrepriseForValideurDialog.nom;
			filteredSignataire = this.valideursPouvantSignerCePdp.filter(signataireExt => signataireExt.nomEntreprise === nomEntreprise);
		}
		if (this.valideursDuPdp.length === 0) {
			// tous les signataires sont dans la colonne de gauche (aucun signataire lie)
			signatairesLeftColumn = filteredSignataire;
		} else if (this.valideursDuPdp.length === filteredSignataire.length
			&& this.valideursDuPdp.every(valideurLie => filteredSignataire.some(signataireExt => signataireExt.id === valideurLie.intervenantId))
			&& filteredSignataire.every(signataireExt => this.valideursDuPdp.some(valideurLie => valideurLie.intervenantId === signataireExt.id))
		) {
			//tous les signataires disponibles sont lies (colonne de droite)
			signatairesLeftColumn = [];
		} else {
			//enlever de la colonne gauche les signataires lies
			signatairesLeftColumn = filteredSignataire.filter(signataireExt => this.valideursDuPdp.every(valideurLie => valideurLie.intervenantId !== signataireExt.id));
		}
		this.gererValideursCommands.next([
			signatairesLeftColumn, 
			this.valideursDuPdp.map( $ => this.valideurToSignataire($) )
		]);
	}

	addRepresentant() {
		let signatairesLeftColumn: SignatairePdpEntity[] = [];
		let filteredSignataire : SignatairePdpEntity[] = this.representantsPouvantSignerCePdp;
		if (this.selectedEntrepriseForRepresentantDialog) {
			const nomEntreprise = this.selectedEntrepriseForRepresentantDialog.nom;
			filteredSignataire = this.representantsPouvantSignerCePdp.filter(signataireExt => signataireExt.nomEntreprise === nomEntreprise);
		}
		if (this.representantsDuPdp.length === 0) {
			// tous les signataires sont dans la colonne de gauche (aucun signataire lie)
			signatairesLeftColumn = filteredSignataire;
		} else if (this.representantsDuPdp.length === filteredSignataire.length
			&& this.representantsDuPdp.every(representantLie => filteredSignataire.some(signataireExt => signataireExt.id === representantLie.intervenantId))
			&& filteredSignataire.every(signataireExt => this.representantsDuPdp.some(representantLie => representantLie.intervenantId === signataireExt.id))
		) {
			//tous les signataires disponibles sont lies (colonne de droite)
			signatairesLeftColumn = [];
		} else {
			//enlever de la colonne gauche les signataires lies
			signatairesLeftColumn = filteredSignataire.filter(signataireExt => this.representantsDuPdp.every(representantLie => representantLie.intervenantId !== signataireExt.id));
		}
		
		// enlever de la colonne gauche les personnes appartenant à des entreprises qui ont déjà des representants
		const entreprisesDejaRepresentees = this.representantsDuPdp.map( $ => $.intervenantEntrepriseId );
		signatairesLeftColumn = signatairesLeftColumn.filter( $ => !entreprisesDejaRepresentees.includes($.idEntreprise) );
		
		this.gererRepresentantsCommands.next([
			signatairesLeftColumn, 
			this.representantsDuPdp.map( $ => this.representantToSignataire($) )
		]);
	}

	addIntervenant() {
		let signatairesLeftColumn: SignatairePdpEntity[] = [];
		let filteredSignataire : SignatairePdpEntity[] = this.intervenantsPouvantSignerCePdp;
		if (this.selectedEntrepriseForIntervenantDialog) {
			const nomEntreprise = this.selectedEntrepriseForIntervenantDialog.nom;
			filteredSignataire = this.intervenantsPouvantSignerCePdp.filter(signataireExt => signataireExt.nomEntreprise === nomEntreprise);
		}
		if (this.intervenantsDuPdp.length === 0) {
			// tous les signataires sont dans la colonne de gauche (aucun signataire lie)
			signatairesLeftColumn = filteredSignataire;
		} else if (this.intervenantsDuPdp.length === filteredSignataire.length
			&& this.intervenantsDuPdp.every(signataireLie => filteredSignataire.some(signataireExt => signataireExt.id === signataireLie.id))
			&& filteredSignataire.every(signataireExt => this.intervenantsDuPdp.some(signataireLie => signataireLie.id === signataireExt.id))
		) {
			//tous les signataires disponibles sont lies (colonne de droite)
			signatairesLeftColumn = [];
		} else {
			//enlever de la colonne gauche les signataires lies
			signatairesLeftColumn = filteredSignataire.filter(signataireExt => this.intervenantsDuPdp.every(signataireLie => signataireLie.id !== signataireExt.id));
		}

		this.gererIntervenantsCommands.next([
			signatairesLeftColumn, 
			this.intervenantsDuPdp
		]);
	}

	onChangeTypeValideur(index: number) {
		console.log('onChangeTypeValideur sur ligne: ', index);
		this.updateListeIntervenantsOnValideursChange();
		this.updateListeRepresentantsOnValideursChange();
	}

	onCheckRepresentantIntervenant(representant: RepresentantEntity, index	: number) {
		// mettre à jour le tableau des intervenants
		if (representant.isIntervenant) {
			this.intervenantsDuPdp.push(this.representantToSignataire(representant));
			this.intervenantsDuPdp.sort(
				(s1, s2) => s1.nomEntreprise.localeCompare(s2.nomEntreprise) || s1.nom.localeCompare(s2.nom)
			);
			// si cette personne est dans la liste des valideurs faut lui ajouter le type intervenant
			this.valideursDuPdp.filter(v => v.intervenantId === representant.intervenantId).forEach( valideur => {
				switch (valideur.typeValideur) {
					case TypeValideur.VALIDEUR_REPRESENTANT:
						// ce valideur devient VALIDEUR_REPRESENTANT_INTERVENANT
						valideur.typeValideur = TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT;
						break;
					case TypeValideur.VALIDEUR:
						// ce valideur devient VALIDEUR_INTERVENANT
						valideur.typeValideur = TypeValideur.VALIDEUR_INTERVENANT;
						break;
					default:
						// rien à faire		
				}
			} );
		} else {
			this.intervenantsDuPdp = this.intervenantsDuPdp.filter(v => v.id !== representant.intervenantId);
			// si cette personne est dans la liste des valideurs faut lui retirer le type intervenant
			this.valideursDuPdp.filter(v => v.intervenantId === representant.intervenantId).forEach( valideur => {
				switch (valideur.typeValideur) {
					case TypeValideur.VALIDEUR_INTERVENANT:
						// ce valideur devient VALIDEUR
						valideur.typeValideur = TypeValideur.VALIDEUR;
						break;
					case TypeValideur.VALIDEUR_REPRESENTANT_INTERVENANT:
						// ce valideur devient VALIDEUR_REPRESENTANT
						valideur.typeValideur = TypeValideur.VALIDEUR_REPRESENTANT;
						break;
					default:
						// rien à faire		
				}
			} );
		}
	}
		
	majValideurEmailInOtherListes(valideur: ValideurSignataireEntity) {
		this.representantsDuPdp.forEach( $ => {
			if (valideur.intervenantId === $.intervenantId) {
				$.email = valideur.email;
				if (!applicationConstants.emailRegexp.test($.email)) {
					$.sentToDocusign = false;
					$.emailStyle = 'error';
				} else {
					$.emailStyle = '';
				}
			}
		} );
		this.intervenantsDuPdp.forEach( $ => {
			if (valideur.intervenantId === $.id) {
				$.email = valideur.email;
				if (!applicationConstants.emailRegexp.test($.email)) {
					$.sentToDocusign = false;
					$.emailStyle = 'error';
				} else {
					$.emailStyle = '';
				}
			}
		} );
	}
		
	majRepresentantEmailInOtherListes(representant: RepresentantEntity) {
		this.valideursDuPdp.forEach( $ => {
			if (representant.intervenantId === $.intervenantId) {
				$.email = representant.email;
				if (!applicationConstants.emailRegexp.test($.email)) {
					$.sentToDocusign = false;
					$.emailStyle = 'error';
				} else {
					$.emailStyle = '';
				}
			}
		} );
		this.intervenantsDuPdp.forEach( $ => {
			if (representant.intervenantId === $.id) {
				$.email = representant.email;
				if (!applicationConstants.emailRegexp.test($.email)) {
					$.sentToDocusign = false;
					$.emailStyle = 'error';
				} else {
					$.emailStyle = '';
				}
			}
		} );
	}
		
	majIntervenantEmailInOtherListes(intervenant: SignatairePdpEntity) {
		this.valideursDuPdp.forEach( $ => {
			if (intervenant.id === $.intervenantId) {
				$.email = intervenant.email;
				if (!applicationConstants.emailRegexp.test($.email)) {
					$.sentToDocusign = false;
					$.emailStyle = 'error';
				} else {
					$.emailStyle = '';
				}
			}
		} );
		this.representantsDuPdp.forEach( $ => {
			if (intervenant.id === $.intervenantId) {
				$.email = intervenant.email;
				if (!applicationConstants.emailRegexp.test($.email)) {
					$.sentToDocusign = false;
					$.emailStyle = 'error';
				} else {
					$.emailStyle = '';
				}
			}
		} );
	}
	
	onFocusLeaveEmailValideur(valideur: ValideurSignataireEntity) {
		if (valideur.email) {
			valideur.email = valideur.email.trim();
			if (!applicationConstants.emailRegexp.test(valideur.email)) {
				valideur.sentToDocusign = false;
				valideur.emailStyle = 'error';
			} else {
				valideur.emailStyle = '';
			}
		}
		this.refreshValideursSendCbs();
		this.sendAllValideursInEntrepriseCb[valideur.intervenantEntrepriseId] = this.valideursDuPdp
			.filter( v => v.intervenantEntrepriseId === valideur.intervenantEntrepriseId )
			.every( v => v.sentToDocusign );
		this.checkIfShouldDocusign();
		// maj de l'email dans les autres listes si cette personne y figure
		this.majValideurEmailInOtherListes(valideur);
	}
	
	onFocusLeaveEmailRepresentant(representant: RepresentantEntity) {
		if (representant.email) {
			representant.email = representant.email.trim();
			if (!applicationConstants.emailRegexp.test(representant.email)) {
				representant.sentToDocusign = false;
				representant.emailStyle = 'error';
			} else {
				representant.emailStyle = '';
			}
		}
		this.refreshRepresentantsSendCbs();
		this.sendAllRepresentantsInEntrepriseCb[representant.intervenantEntrepriseId] = this.representantsDuPdp
			.filter( r => r.intervenantEntrepriseId === representant.intervenantEntrepriseId )
			.every( r => r.sentToDocusign );
		this.checkIfShouldDocusign();
		// maj de l'email dans les autres listes si cette personne y figure
		this.majRepresentantEmailInOtherListes(representant);
	}

	onFocusLeaveEmailIntervenant(intervenant: SignatairePdpEntity) {
		if (intervenant.email) {
			intervenant.email = intervenant.email.trim();
			if (!applicationConstants.emailRegexp.test(intervenant.email)) {
				intervenant.sentToDocusign = false;
				intervenant.emailStyle = 'error';
			} else {
				intervenant.emailStyle = '';
			}
		}
		this.refreshIntervenantsSendCbs();
		this.sendAllIntervenantsInEntrepriseCb[intervenant.idEntreprise] = this.intervenantsDuPdp
			.filter( i => i.idEntreprise === intervenant.idEntreprise )
			.every( i => i.sentToDocusign );
		this.checkIfShouldDocusign();
		// maj de l'email dans les autres listes si cette personne y figure
		this.majIntervenantEmailInOtherListes(intervenant);
	}
	
	refreshValideursSendCbs() {
		setTimeout(() => {
			this.sendAllValideursCb = this.valideursDuPdp.length > 0 && this.valideursDuPdp.every( v => v.sentToDocusign );
			this.entrepriseValideurIds.forEach( enterpriseId => {
				const entepriseValideurs = this.valideursDuPdp.filter( v => v.intervenantEntrepriseId === enterpriseId );
				this.sendAllValideursInEntrepriseCb[enterpriseId] = entepriseValideurs.length > 0 && entepriseValideurs.every( v => v.sentToDocusign );
			});
			this.updateOtherListesOnValideurSendCbChange();
		}, 100);
	}
	
	updateOtherListesOnValideurSendCbChange(){
		this.valideursDuPdp.forEach( v => {
			this.intervenantsDuPdp.forEach( i => {
				if (v.intervenantId === i.id && (i.envelopeId === null || i.envelopeId === '')) {
					i.sentToDocusign = v.sentToDocusign;
				}
			} );
			this.representantsDuPdp.forEach( r => {
				if (v.intervenantId === r.intervenantId && (r.docusignEnvelopeId === null || r.docusignEnvelopeId === -1)) {
					r.sentToDocusign = v.sentToDocusign;
				}
			} );
		} );
		this.entrepriseExterneIds.forEach( enterpriseId => {
			const entepriseIntervenants = this.intervenantsDuPdp.filter( i => i.idEntreprise === enterpriseId );
			this.sendAllIntervenantsInEntrepriseCb[enterpriseId] = entepriseIntervenants.length > 0 && entepriseIntervenants.every( i => i.sentToDocusign );
			
			const entepriseRepresentants = this.representantsDuPdp.filter( r => r.intervenantEntrepriseId === enterpriseId );
			this.sendAllRepresentantsInEntrepriseCb[enterpriseId] = entepriseRepresentants.length > 0 && entepriseRepresentants.every( r => r.sentToDocusign );
		});
	}
	
	updateOtherListesOnRepresentantSendCbChange(){
		this.representantsDuPdp.forEach( r => {
			this.intervenantsDuPdp.forEach( i => {
				i.updatable = i.envelopeId != null || i.envelopeId != '';
				if (r.intervenantEntrepriseId === i.idEntreprise && (i.envelopeId === null || i.envelopeId === '')) {
					i.updatable = false;
					if (r.sentToDocusign) {
						if (i.emailStyle !== 'error'){
							i.sentToDocusign = true;
						}
					} else {
						i.sentToDocusign = false;
					}
				}
			} );
			this.valideursDuPdp.forEach( v => {
				if (r.intervenantId === v.intervenantId && (v.docusignEnvelopeId === null || v.docusignEnvelopeId === -1)) {
					v.sentToDocusign = r.sentToDocusign;
				}
			} );
		} );
		this.entrepriseExterneIds.forEach( enterpriseId => {
			const entepriseIntervenants = this.intervenantsDuPdp.filter( i => i.idEntreprise === enterpriseId );
			this.sendAllIntervenantsInEntrepriseCb[enterpriseId] = entepriseIntervenants.length > 0 && entepriseIntervenants.every( i => i.sentToDocusign );
		});
		this.entrepriseValideurIds.forEach( enterpriseId => {
			const entepriseValideurs = this.valideursDuPdp.filter( v => v.intervenantEntrepriseId === enterpriseId );
			this.sendAllValideursInEntrepriseCb[enterpriseId] = entepriseValideurs.length > 0 && entepriseValideurs.every( v => v.sentToDocusign );
		});
	}

	updateOtherListesOnIntervenantSendCbChange(){
		this.intervenantsDuPdp.forEach( i => {
			this.representantsDuPdp.forEach( r => {
				if (r.intervenantId === i.id && (r.docusignEnvelopeId === null || r.docusignEnvelopeId === -1)) {
					r.sentToDocusign = i.sentToDocusign;
				}
			} );
			this.valideursDuPdp.forEach( v => {
				if (i.id === v.intervenantId && (v.docusignEnvelopeId === null || v.docusignEnvelopeId === -1)) {
					v.sentToDocusign = i.sentToDocusign;
				}
			} );
		} );
		this.entrepriseValideurIds.forEach( enterpriseId => {
			const entepriseValideurs = this.valideursDuPdp.filter( v => v.intervenantEntrepriseId === enterpriseId );
			this.sendAllValideursInEntrepriseCb[enterpriseId] = entepriseValideurs.length > 0 && entepriseValideurs.every( v => v.sentToDocusign );
		});
		this.entrepriseExterneIds.forEach( enterpriseId => {
			const entepriseRepresentants = this.representantsDuPdp.filter( r => r.intervenantEntrepriseId === enterpriseId );
			this.sendAllRepresentantsInEntrepriseCb[enterpriseId] = entepriseRepresentants.length > 0 && entepriseRepresentants.every( r => r.sentToDocusign );
		});
	}

	refreshValideursSignCbs() {
		this.signAllValideursCb = this.valideursDuPdp.every( v => v.hasSigned );
	}

	refreshRepresentantsSignCbs() {
		this.signAllRepresentantsCb = this.representantsDuPdp.every( r => r.hasSigned );
	}

	refreshIntervenantsSignCbs() {
		this.signAllIntervenantsCb = this.intervenantsDuPdp.every( i => i.hasSigned );
	}

	refreshRepresentantsSendCbs() {
		setTimeout(() => {
			this.sendAllRepresentantsCb = this.representantsDuPdp.length > 0 && this.representantsDuPdp.every( r => r.sentToDocusign );
			this.entrepriseExterneIds.forEach( enterpriseId => {
				const entepriseRepresentants = this.representantsDuPdp
					.filter( r => r.intervenantEntrepriseId === enterpriseId );
				this.sendAllRepresentantsInEntrepriseCb[enterpriseId] = entepriseRepresentants.length > 0 && entepriseRepresentants.every( r => r.sentToDocusign );
			});
			this.updateOtherListesOnRepresentantSendCbChange();
		}, 100);
	}

	refreshIntervenantsSendCbs() {
		setTimeout(() => {
			this.sendAllIntervenantsCb = this.intervenantsDuPdp.length > 0 && this.intervenantsDuPdp.every( i => i.sentToDocusign );
			this.entrepriseExterneIds.forEach( enterpriseId => {
				const entepriseIntervenants = this.intervenantsDuPdp
					.filter( i => i.idEntreprise === enterpriseId );
				this.sendAllIntervenantsInEntrepriseCb[enterpriseId] = entepriseIntervenants.length > 0 && entepriseIntervenants.every( i => i.sentToDocusign );
			});
			this.updateOtherListesOnIntervenantSendCbChange();
		}, 100);
	}
	
	onCheckSendAllValideurs(value: MatCheckboxChange){
		this.valideursDuPdp.forEach( v => {
			if (v.email && v.email.trim() && applicationConstants.emailRegexp.test(v.email) && (v.docusignEnvelopeId == null || v.docusignEnvelopeId === -1)) {
				v.sentToDocusign = value.checked;
			}
		} );
		this.refreshValideursSendCbs();
		this.checkIfShouldDocusign();
	}	
	
	onCheckSignAllValideurs(value: MatCheckboxChange){
		this.valideursDuPdp.forEach( v => {
			if (!v.hasSignedEnvelope) {
				v.hasSigned = value.checked;
			}
		} )
	}

	onCheckSendAllValideursInEntreprise(value: MatCheckboxChange, entrepriseId: number){
		this.valideursDuPdp.filter( v => v.intervenantEntrepriseId === entrepriseId ).forEach( v => {
			if (v.email && v.email.trim() && applicationConstants.emailRegexp.test(v.email) && (v.docusignEnvelopeId == null || v.docusignEnvelopeId === -1)) {
				v.sentToDocusign = value.checked;
			}
		} );
		this.refreshValideursSendCbs();
		this.checkIfShouldDocusign();
	}
	
	onCheckSendAllRepresentants(value: MatCheckboxChange){
		this.representantsDuPdp.forEach( r => {
			if (r.email && r.email.trim() && applicationConstants.emailRegexp.test(r.email) && (r.docusignEnvelopeId == null || r.docusignEnvelopeId === -1)) {
				r.sentToDocusign = value.checked;
			}
		} );
		this.refreshRepresentantsSendCbs();
		this.checkIfShouldDocusign();
	}

	onCheckSignAllRepresentants(value: MatCheckboxChange){
		this.representantsDuPdp.forEach( r => {
			if (!r.hasSignedEnvelope) {
				r.hasSigned = value.checked;
				// trouver tous les intervenants du même entreprise qui n'ont pas encore signés 
				// cocher leurs cases à la valeur de cette case à cocher
				this.checkSignIntervenantsSubalterne(value, r);
			}
		} )
	}
	
	checkSignIntervenantsSubalterne(value: MatCheckboxChange, representant: RepresentantEntity) {
		// trouver tous les intervenants - du même entreprise que ce representant - qui n'ont pas encore signés 
		// cocher leurs cases à la valeur de cette case à cocher
		this.intervenantsDuPdp.filter( i => i.idEntreprise === representant.intervenantEntrepriseId )
			.filter( i => !i.hasSignedEnvelope ).forEach( i => i.hasSigned = value.checked );
	}

	onCheckSendAllRepresentantsInEntreprise(value: MatCheckboxChange, entrepriseId: number){
		this.representantsDuPdp.filter( r => r.intervenantEntrepriseId === entrepriseId ).forEach( r => {
			if (r.email && r.email.trim() && applicationConstants.emailRegexp.test(r.email) && (r.docusignEnvelopeId == null || r.docusignEnvelopeId === -1)) {
				r.sentToDocusign = value.checked;
			}
		} );
		this.refreshRepresentantsSendCbs();
		this.checkIfShouldDocusign();
	}

	onCheckSendAllIntervenants(value: MatCheckboxChange){
		this.intervenantsDuPdp.forEach( i => {
			if (i.email && i.email.trim() && applicationConstants.emailRegexp.test(i.email) && !i.envelopeId) {
				i.sentToDocusign = value.checked;
			}
		} );
		this.refreshIntervenantsSendCbs();
		this.checkIfShouldDocusign();
	}

	onCheckSignAllIntervenants(value: MatCheckboxChange){
		this.intervenantsDuPdp.forEach( i => {
			if (!i.hasSignedEnvelope) {
				i.hasSigned = value.checked;
			}
		} )
	}

	onCheckSendAllIntervenantsInEntreprise(value: MatCheckboxChange, entrepriseId: number){
		this.intervenantsDuPdp.filter( r => r.idEntreprise === entrepriseId ).forEach( i => {
			if (i.email && i.email.trim() && applicationConstants.emailRegexp.test(i.email) && !i.envelopeId) {
				i.sentToDocusign = value.checked;
			}
		} );
		this.refreshIntervenantsSendCbs();
		this.checkIfShouldDocusign();
	}

	checkIfShouldDocusign(){
		this.hasAtLeastOneSigner = this.valideursDuPdp
			.filter( v => v.email && v.email.trim() && !v.docusignEnvelopeId && v.emailStyle !== 'error' )
			.some( v => v.sentToDocusign );
		this.hasAtLeastOneSigner = this.hasAtLeastOneSigner || this.representantsDuPdp
			.filter( r => r.email && r.email.trim() && !r.docusignEnvelopeId && r.emailStyle !== 'error' )
			.some( r => r.sentToDocusign );
		this.hasAtLeastOneSigner = this.hasAtLeastOneSigner || this.intervenantsDuPdp
			.filter( i => i.email && i.email.trim() && !i.envelopeId && i.emailStyle !== 'error' )
			.some( i => i.sentToDocusign );
	}

	onSaveRequested(){
		this.waitingForServer = true;
		this.pdp.valideurSignataires = this.valideursDuPdp;
		this.pdp.signataires = this.intervenantsDuPdp;
		this.pdp.representants = this.representantsDuPdp;
		this.planPreventionService.updateSignatairesOnGeneralPdp(this.pdp).subscribe(
			(result) => {
				this.waitingForServer = false;
				this.eventBusService.emit(Events.RefreshBatchIndicatorRequested, null);
				this.cleanAndClose();
			},
			(httpErrorResponse: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.eventBusService.emit(Events.RefreshBatchIndicatorRequested, null);
				
				console.log(httpErrorResponse);
				let error = httpErrorResponse.error;
				if (error != null) {
					if (error.exceptionClass === 'com.engie.aurora.exceptions.BadRequestException' && error.message) {
						alert(error.message);
						return;
					} else if (error.exceptionClass === 'org.hibernate.exception.ConstraintViolationException') {
						alert('Le serveur a rencontré un problème lors de la sauvegarde des données, pour rappel l\'email est une donnée unique par signataire');
						return;
					}
				} else {
					alert('La requête n\'a pas pu être traitée à cause d\'un problème technique');
				}
			}
		);
	}
	
	determineDestinataires(): Destinataire[]{
		let destinataires: Destinataire[] = [];
		this.representantsDuPdp.forEach( representant => {
			if ((representant.docusignEnvelopeId == null || representant.docusignEnvelopeId === -1) && representant.sentToDocusign) {
				// représentant nouvellement coché
				let destinataire: Destinataire = new Destinataire();
				destinataire.email = representant.email;
				destinataire.prenom = representant.intervenantPrenom;
				destinataire.nom = representant.intervenantNom;
				destinataire.allRoles.push(TypeActeur.REPRESENTANT);
				if (this.intervenantsDuPdp.some( $ => $.id === representant.intervenantId )) {
					// il est aussi INTERVENANT
					destinataire.allRoles.push(TypeActeur.INTERVENANT);
				}
				if (this.valideursDuPdp.some( $ => $.intervenantId === representant.intervenantId )) {
					// il est aussi VALIDEUR
					destinataire.allRoles.push(TypeActeur.VALIDEUR);
				}
				destinataire.typeActeur = destinataire.allRoles[0];
				destinataires.push(destinataire);
			}
		} );
		this.intervenantsDuPdp.forEach( intervenant => {
			if (!intervenant.envelopeId  && intervenant.sentToDocusign && !destinataires.some( $ => $.email === intervenant.email )) {
				if (!this.representantsDuPdp.some( r => r.sentToDocusign && r.intervenantEntrepriseId === intervenant.idEntreprise ) ) {
					// intervenant nouvellement coché
					let destinataire: Destinataire = new Destinataire();
					destinataire.email = intervenant.email;
					destinataire.prenom = intervenant.prenom;
					destinataire.nom = intervenant.nom;
					destinataire.allRoles.push(TypeActeur.INTERVENANT);
					if (this.valideursDuPdp.some( $ => $.intervenantId === intervenant.id )) {
						// il est aussi VALIDEUR
						destinataire.allRoles.push(TypeActeur.VALIDEUR);
					}
					destinataire.typeActeur = destinataire.allRoles[0];
					destinataires.push(destinataire);
				}
			}
		} );
		this.valideursDuPdp.forEach( valideur => {
			if ((valideur.docusignEnvelopeId == null || valideur.docusignEnvelopeId === -1) && valideur.sentToDocusign && !destinataires.some( $ => $.email === valideur.email )) {
				// valideur nouvellement coché
				let destinataire: Destinataire = new Destinataire();
				destinataire.email = valideur.email;
				destinataire.prenom = valideur.prenom;
				destinataire.nom = valideur.nom;
				destinataire.allRoles.push(TypeActeur.VALIDEUR);
				destinataire.typeActeur = destinataire.allRoles[0];
				destinataires.push(destinataire);
			}
		} );
		
		return destinataires;
	}
	
	onSaveAndSendRequested(){
		let destinataires: Destinataire[] = this.determineDestinataires();
		console.log('destinataires: ', destinataires);
		this.waitingForServer = true;
		this.pdp.valideurSignataires = this.valideursDuPdp;
		this.pdp.signataires = this.intervenantsDuPdp;
		this.pdp.representants = this.representantsDuPdp;
		this.planPreventionService.updateSignatairesOnGeneralPdp(this.pdp).subscribe(
			(result) => {
				// on a mis à jour les signataires, maintenant il faut envoyer les envelopes
				// on va recupérer les refs puis on envoie les envelopes
				console.log('enregistrement effectué en base de données, poursuite des opérations avec envoie d\'enveloppe: ', result);
				result.representants.forEach( representant => {
					destinataires.forEach( destinataire => {
						if (destinataire.email === representant.email && destinataire.typeActeur === TypeActeur.REPRESENTANT) {
							destinataire.recipientId = representant.docusignRecipientId;
							destinataire.sentToDocusign = true;
						}
					} );
				} );
				result.signataires.forEach( intervenant => {
					destinataires.forEach( destinataire => {
						if (destinataire.email === intervenant.email && destinataire.typeActeur === TypeActeur.INTERVENANT) {
							destinataire.recipientId = intervenant.docusignRecipientId;
							destinataire.sentToDocusign = true;
						}
					} );
				} );
				result.valideurSignataires.forEach( valideur => {
					destinataires.forEach( destinataire => {
						if (destinataire.email === valideur.email && destinataire.typeActeur === TypeActeur.VALIDEUR) {
							destinataire.recipientId = valideur.docusignRecipientId;
							destinataire.sentToDocusign = true;
						}
					} );
				} );
				
				this.planPreventionService.sendEnvelopeOnGeneralPdp(this.pdp.id, destinataires).subscribe(
					(result) => {
						this.waitingForServer = false;
						this.eventBusService.emit(Events.RefreshBatchIndicatorRequested, null);
						this.cleanAndClose();					
					},
					(httpErrorResponse: HttpErrorResponse) => {
						this.waitingForServer = false;
						this.eventBusService.emit(Events.RefreshBatchIndicatorRequested, null);
						
						let error = httpErrorResponse.error;
						if (error != null) {
							if (error.exceptionClass === 'com.engie.aurora.exceptions.BadRequestException' && error.message) {
								alert(error.message);
								return;
							}
						}
						console.log(httpErrorResponse);
						alert('La requête n\'a pas pu être traitée à cause d\'un problème technique');
					}
				);
			},
			(httpErrorResponse: HttpErrorResponse) => {
				this.waitingForServer = false;
				let error = httpErrorResponse.error;
				if (error != null) {
					if (error.exceptionClass === 'com.engie.aurora.exceptions.BadRequestException' && error.message) {
						alert(error.message);
						return;
					}
				}
				console.log(httpErrorResponse);
				alert('La requête n\'a pas pu être traitée à cause d\'un problème technique');
			}
		);
	}

	viewEnveloppeDetail(envelopeId: number | string){
		if (envelopeId) {
			let isEnvelopeRetryable = this.pdp.obsolete || this.pdp.expired ? 'false' : 'true';
			this.viewEnveloppeCommand.next(envelopeId + ';' + this.pdp.id + ';' + isEnvelopeRetryable);
		}
	}

	onEnvelopeCancellation(){
		// declencher le refresh de ce popup
		this.signCommandsSubject.next(this.pdp);
	}
	
	checkIntervenantsSubalterneWhenRepresentantDocusigned(representant: RepresentantEntity) {
		// si ce representant a signé son enveloppe
		// trouver tous les intervenants - du même entreprise
		// cocher et griser leurs cases
		if (representant.hasSignedEnvelope){
			this.intervenantsDuPdp.filter( i => i.idEntreprise === representant.intervenantEntrepriseId )
				.forEach( i => {
					i.hasSigned = true;
					i.updatable = false;
				} );
		}
	}
	
	hisRepresentantHasSigned(intervenant: SignatairePdpEntity): boolean {
		// si cet intervenant appartient à une entreprise et que cette entreprise a un representant qui a signé, on le marque comme "a signé"
		let representantOfIntervenant = this.representantsDuPdp.filter( $ => $.intervenantEntrepriseId === intervenant.idEntreprise );
		if (representantOfIntervenant.length > 0 && representantOfIntervenant[0].hasSigned) {
			return true;
		}
		return false;
	}
	
}